import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FloatingParticles from "../components/FloatingParticles";
const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";

// Helper: format timestamp to "2 hours ago" etc.
function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  const diff = Math.floor((now - date) / 1000);

  if (diff < 0) return "Just now"; // ✅ handles future/wrong timestamps
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}
const TABS = ["All", "Unread", "Read"];

export default function UserNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");

  let storedUser = null;
  try {
    const raw = localStorage.getItem("user");
    storedUser = raw && raw !== "undefined" ? JSON.parse(raw) : null;
  } catch {
    storedUser = null;
  }

  const userId = storedUser?.id || storedUser?.userId;

  const fetchNotifications = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/notifications/user/${userId}`);
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API}/api/notifications/read/${id}`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: 1 } : n)
      );
    } catch (err) {
      toast.error("Failed to mark notification as read.");
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const filteredNotifications = notifications.filter(n => {
    if (activeTab === "Unread") return !n.isRead;
    if (activeTab === "Read") return n.isRead;
    return true;
  });

  return (
    <div style={{ minHeight: "100vh", fontFamily: "'Outfit', sans-serif", position: "relative" }}>
      <style>{`
        .notif-container { max-width: 900px; margin: 0 auto; padding: 32px 24px 80px; position: relative; z-index: 1; }
        @media (max-width: 600px) {
          .notif-container { padding: 16px 16px 60px; }
        }
      `}</style>
      <FloatingParticles />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="notif-container">

        {/* Header with unread badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: "#0c4a6e", margin: 0 }}>🔔 Notifications</h1>
          {unreadCount > 0 && (
            <span style={{
              background: "#0ea5e9",
              color: "#fff",
              borderRadius: 999,
              padding: "2px 10px",
              fontSize: 13,
              fontWeight: 700,
              lineHeight: "22px"
            }}>
              {unreadCount} unread
            </span>
          )}
        </div>

        {/* Filter Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: "8px 20px",
                borderRadius: 999,
                border: "1px solid",
                borderColor: activeTab === tab ? "#0ea5e9" : "#e2e8f0",
                background: activeTab === tab ? "#0ea5e9" : "#fff",
                color: activeTab === tab ? "#fff" : "#64748b",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                transition: "all 0.2s",
                fontFamily: "'Outfit', sans-serif"
              }}
            >
              {tab}
              {tab === "Unread" && unreadCount > 0 && (
                <span style={{
                  marginLeft: 6,
                  background: activeTab === "Unread" ? "rgba(255,255,255,0.3)" : "#0ea5e9",
                  color: "#fff",
                  borderRadius: 999,
                  padding: "1px 7px",
                  fontSize: 11,
                  fontWeight: 700,
                }}>
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Notification List */}
        {loading ? (
          <p style={{ color: "#94a3b8", textAlign: "center", marginTop: 40 }}>Loading your notifications...</p>
        ) : filteredNotifications.length === 0 ? (
          <p style={{ color: "#64748b", textAlign: "center", marginTop: 40 }}>
            {activeTab === "All" ? "No notifications at the moment." : `No ${activeTab.toLowerCase()} notifications.`}
          </p>
        ) : (
          <div style={{ marginTop: 8 }}>
            {filteredNotifications.map((notif) => (
              <div key={notif.id} style={{
                background: notif.isRead ? "#f8fafc" : "#fff",
                borderRadius: 16,
                padding: 20,
                marginBottom: 16,
                border: "1px solid #e2e8f0",
                boxShadow: notif.isRead ? "none" : "0 4px 12px rgba(14,165,233,0.1)",
                transition: "all 0.3s ease"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <p style={{ margin: 0, fontWeight: 600, color: "#0f172a" }}>{notif.title}</p>
                  {/* Timestamp */}
                  <span style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap", marginLeft: 12 }}>
                    {notif.createdAt ? timeAgo(notif.createdAt) : ""}
                  </span>
                </div>
                <p style={{ margin: "4px 0 0", color: "#475569", fontSize: 14 }}>{notif.message}</p>
                {!notif.isRead && (
                  <button
                    onClick={() => markAsRead(notif.id)}
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      color: "#0ea5e9",
                      fontWeight: 600,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0
                    }}
                  >
                    Mark as read
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}